import React, { useEffect, useMemo, useState } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import moment from 'moment'
import { Calendar, ClockEight } from '../../../assets/icons'
import DatePickerIconInput from '../../ui/input/datePickerIconInput'
import { addMonthsPreservingDay, createRangeDayClassName } from './utils'
import { DatePickerWrapper, Group, InlineErrorText, Label, TimeRangeRow } from './styled'

registerLocale('es', es)

const parseDateValue = value => {
	if (!value) return null
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

	const parsed = new Date(value)
	return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDateRangeInputValue = (dateFrom, dateTo) => {
	const from = parseDateValue(dateFrom)
	const to = parseDateValue(dateTo)

	if (!from && !to) return ''
	if (from && !to) return moment(from).format('DD/MM/YYYY')
	if (!from && to) return moment(to).format('DD/MM/YYYY')

	return `${moment(from).format('DD/MM/YYYY')} - ${moment(to).format('DD/MM/YYYY')}`
}

const applyDateRangeChange = ({
	dates,
	values,
	setFieldValue,
	startDateField,
	endDateField,
	onRangeCompleted
}) => {
	if (!dates) {
		setFieldValue(startDateField, null)
		setFieldValue(endDateField, null)
		return
	}

	if (Array.isArray(dates)) {
		const [rawFrom, rawTo] = dates
		const from = parseDateValue(rawFrom)
		let to = parseDateValue(rawTo)

		setFieldValue(startDateField, from)
		setFieldValue(endDateField, to)
		if (to && onRangeCompleted) onRangeCompleted()
		return
	}

	const pickedDate = parseDateValue(dates)
	const currentFrom = parseDateValue(values[startDateField])
	const currentTo = parseDateValue(values[endDateField])

	if (!pickedDate) {
		setFieldValue(startDateField, null)
		setFieldValue(endDateField, null)
		return
	}

	if (!currentFrom || currentTo) {
		setFieldValue(startDateField, pickedDate)
		setFieldValue(endDateField, null)
		return
	}

	let from = currentFrom
	let to = pickedDate

	if (currentFrom.getTime() > pickedDate.getTime()) {
		from = pickedDate
		to = currentFrom
	}

	setFieldValue(startDateField, from)
	setFieldValue(endDateField, to)
	if (onRangeCompleted) onRangeCompleted()
}

const toMinutes = date => {
	if (!date) return null
	const parsedDate = new Date(date)
	if (Number.isNaN(parsedDate.getTime())) return null
	return parsedDate.getHours() * 60 + parsedDate.getMinutes()
}

const DefaultInlineError = ({ message }) =>
	message ? <InlineErrorText>{message}</InlineErrorText> : null

const DateTimeRangeFilters = ({
	values,
	setFieldValue,
	errors = {},
	InlineErrorComponent = DefaultInlineError,
	startDateField = 'dateFrom',
	endDateField = 'dateTo',
	hourFromField = 'hourFrom',
	hourToField = 'hourTo',
	minDate,
	maxDate,
	minHourMinutes,
	maxHourMinutes,
	allowedHourMinutes = null,
	timeIntervals = 30,
	dateLabel = 'Fecha del turno',
	timeLabel = 'Rango horario',
	textAlign = 'left'
}) => {
	const InlineError = InlineErrorComponent
	const [isDateRangeOpen, setIsDateRangeOpen] = useState(false)
	const startDateValue = parseDateValue(values[startDateField])
	const endDateValue = parseDateValue(values[endDateField])
	const hourFromValue = values[hourFromField]
	const hourToValue = values[hourToField]
	const hourFromDateValue = parseDateValue(hourFromValue)
	const hourToDateValue = parseDateValue(hourToValue)
	const maxRangeEndDate = startDateValue ? addMonthsPreservingDay(startDateValue, 2) : null
	const dateRangeLimitMessage =
		startDateValue && endDateValue && maxRangeEndDate && endDateValue > maxRangeEndDate
			? 'El rango de fechas no puede superar los 2 meses'
			: null
	const getRangeDayClassName = createRangeDayClassName(startDateValue, endDateValue)
	const allowedHourSet = useMemo(() => {
		if (!Array.isArray(allowedHourMinutes)) return null

		const set = new Set(
			allowedHourMinutes
				.map(value => Number(value))
				.filter(value => Number.isFinite(value) && value >= 0 && value < 24 * 60)
		)

		return set.size > 0 ? set : null
	}, [allowedHourMinutes])
	const isAllowedHourFrom = time => {
		const optionMinutes = toMinutes(time)
		if (optionMinutes == null) return true

		if (minHourMinutes != null && optionMinutes < minHourMinutes) return false
		if (maxHourMinutes != null && optionMinutes > maxHourMinutes) return false

		if (!allowedHourSet) return true
		return allowedHourSet.has(optionMinutes)
	}
	const isAllowedHourTo = time => {
		const optionMinutes = toMinutes(time)
		if (optionMinutes == null) return true

		if (minHourMinutes != null && optionMinutes < minHourMinutes) return false
		if (maxHourMinutes != null && optionMinutes > maxHourMinutes) return false

		if (allowedHourSet && !allowedHourSet.has(optionMinutes)) return false
		if (!hourFromDateValue) return true
		const hourFromMinutes = toMinutes(hourFromDateValue)
		if (hourFromMinutes == null) return true
		return optionMinutes >= hourFromMinutes
	}

	const isWithinHourRange = minutes => {
		if (minutes == null) return true
		if (minHourMinutes != null && minutes < minHourMinutes) return false
		if (maxHourMinutes != null && minutes > maxHourMinutes) return false
		return true
	}

	useEffect(() => {
		if (!hourFromValue) return

		const hourFromMinutes = toMinutes(hourFromValue)
		const isOutsideHourRange = !isWithinHourRange(hourFromMinutes)
		const isOutsideAllowedSet = allowedHourSet && !allowedHourSet.has(hourFromMinutes)

		if (hourFromMinutes != null && (isOutsideHourRange || isOutsideAllowedSet)) {
			setFieldValue(hourFromField, null)
		}
	}, [hourFromValue, hourFromField, allowedHourSet, minHourMinutes, maxHourMinutes, setFieldValue])

	useEffect(() => {
		if (!hourToValue) return

		const hourToMinutes = toMinutes(hourToValue)
		const isOutsideHourRange = !isWithinHourRange(hourToMinutes)
		const isOutsideAllowedSet = allowedHourSet && !allowedHourSet.has(hourToMinutes)

		if (hourToMinutes != null && (isOutsideHourRange || isOutsideAllowedSet)) {
			setFieldValue(hourToField, null)
		}
	}, [hourToValue, hourToField, allowedHourSet, minHourMinutes, maxHourMinutes, setFieldValue])

	useEffect(() => {
		if (!hourFromValue || !hourToValue) return

		const hourFromMinutes = toMinutes(hourFromValue)
		const hourToMinutes = toMinutes(hourToValue)

		if (hourFromMinutes != null && hourToMinutes != null && hourToMinutes < hourFromMinutes) {
			setFieldValue(hourToField, null)
		}
	}, [hourFromValue, hourToValue, hourToField, setFieldValue])

	return (
		<>
			<Group>
				<Label $textAlign={textAlign}>{dateLabel}</Label>
				<DatePickerWrapper>
					<DatePicker
						selectsRange
						startDate={startDateValue}
						endDate={endDateValue}
						value={formatDateRangeInputValue(startDateValue, endDateValue)}
						open={isDateRangeOpen}
						onInputClick={() => setIsDateRangeOpen(true)}
						onClickOutside={() => setIsDateRangeOpen(false)}
						shouldCloseOnSelect={false}
						onChange={dates =>
							applyDateRangeChange({
								dates,
								values,
								setFieldValue,
								startDateField,
								endDateField,
								onRangeCompleted: () => setIsDateRangeOpen(false)
							})
						}
						dayClassName={getRangeDayClassName}
						placeholderText="Seleccioná una fecha"
						locale="es"
						dateFormat="dd/MM/yyyy"
						minDate={minDate}
						maxDate={maxDate}
						customInput={<DatePickerIconInput icon={<Calendar />} />}
						isClearable
					/>
				</DatePickerWrapper>
				<InlineError message={errors.dateRange || dateRangeLimitMessage} />
			</Group>

			<Group>
				<Label $textAlign={textAlign}>{timeLabel}</Label>
				<DatePickerWrapper>
					<TimeRangeRow>
						<DatePicker
							popperClassName="time-range-popper"
							calendarClassName="time-range-calendar"
							selected={hourFromDateValue}
							onChange={date => setFieldValue(hourFromField, date)}
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={timeIntervals}
							filterTime={isAllowedHourFrom}
							timeCaption="Desde"
							dateFormat="HH:mm"
							timeFormat="HH:mm"
							placeholderText="00:00"
							customInput={
								<DatePickerIconInput icon={<ClockEight />} inputClassName="time-range-input" />
							}
							isClearable
						/>
						<DatePicker
							popperClassName="time-range-popper"
							calendarClassName="time-range-calendar"
							selected={hourToDateValue}
							onChange={date => setFieldValue(hourToField, date)}
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={timeIntervals}
							filterTime={isAllowedHourTo}
							timeCaption="Hasta"
							dateFormat="HH:mm"
							timeFormat="HH:mm"
							placeholderText="00:00"
							customInput={
								<DatePickerIconInput icon={<ClockEight />} inputClassName="time-range-input" />
							}
							isClearable
						/>
					</TimeRangeRow>
				</DatePickerWrapper>
				<InlineError message={errors.timeRange} />
			</Group>
		</>
	)
}

export default DateTimeRangeFilters
